import type { SCurveData, SCurvePoint } from '../../../api/sCurveApi';

/** Fractions of 1 (`0.08` = 8%) versus values already on a 0–100 scale. */
export type SCurvePercentScale = 'fraction' | 'unit';

export interface SCurveChartRow {
  periodLabel: string;
  /** Display percent, 0–100. Null when the API omitted the actual after asOf. */
  planned: number | null;
  actual: number | null;
  plannedPeriod: number | null;
  actualPeriod: number | null;
  plannedCumulative: number | null;
  actualCumulative: number | null;
  /** Actual display percent minus planned display percent. Null when actual is null. */
  variance: number | null;
}

export interface SCurveChartModel {
  rows: SCurveChartRow[];
  scale: SCurvePercentScale;
  showPeriodAmounts: boolean;
  estimateNote: string | null;
}

function finiteOrNull(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  return value;
}

/**
 * Sample percents are fractions. If any non-null cumulative percent is above 1,
 * the series is already 0–100 and must not be multiplied again.
 */
export function detectPercentScale(points: SCurvePoint[]): SCurvePercentScale {
  for (const point of points) {
    for (const value of [point.plannedCumulativePercent, point.actualCumulativePercent]) {
      if (value == null || !Number.isFinite(value)) continue;
      if (value > 1) return 'unit';
    }
  }
  return 'fraction';
}

export function toDisplayPercent(value: number | null | undefined, scale: SCurvePercentScale): number | null {
  const finite = finiteOrNull(value);
  if (finite == null) return null;
  return scale === 'fraction' ? finite * 100 : finite;
}

function hasPeriodAmount(point: SCurvePoint): boolean {
  return finiteOrNull(point.plannedPeriod) != null || finiteOrNull(point.actualPeriod) != null;
}

export function sCurveEstimateNote(data: Pick<SCurveData, 'notes'>): string | null {
  const count = data.notes?.actualMethod?.estimatedFromCurrentPercent;
  if (count == null || !Number.isFinite(count) || count <= 0) return null;
  const tasks = count === 1 ? 'task' : 'tasks';
  return `${count} schedule ${tasks} use an estimated actual: a straight line from the actual start to today at the current percent.`;
}

/** Maps API points onto chart rows. Does not recompute planned or actual progress. */
export function mapSCurveChart(data: SCurveData): SCurveChartModel {
  const scale = detectPercentScale(data.points);
  const showPeriodAmounts = data.points.some(hasPeriodAmount);
  const rows = data.points.map((point) => {
    const planned = toDisplayPercent(point.plannedCumulativePercent, scale);
    const actual = toDisplayPercent(point.actualCumulativePercent, scale);
    return {
      periodLabel: point.periodLabel,
      planned,
      actual,
      plannedPeriod: finiteOrNull(point.plannedPeriod),
      actualPeriod: finiteOrNull(point.actualPeriod),
      plannedCumulative: finiteOrNull(point.plannedCumulative),
      actualCumulative: finiteOrNull(point.actualCumulative),
      variance: actual != null && planned != null ? actual - planned : null,
    };
  });
  return {
    rows,
    scale,
    showPeriodAmounts,
    estimateNote: sCurveEstimateNote(data),
  };
}
